/*
 * Welham & Co: the PDP gallery's videos play when their slide is shown
 * (session W3, 2026-09-24; plan section 3.5 item 1; Improvements I-28).
 *
 * WHY. The control's gallery is the GG slider app, whose install config says
 * `pvi: 1`: a Shopify-hosted video renders as an inline player and starts
 * when its slide becomes the current one, looping (measured from the live
 * house-portrait page, 2026-09-24). Horizon's own gallery shows a poster with
 * a play button instead, so on the variant a shopper who swipes to a video
 * sees a still picture. This file closes that gap and nothing more: the
 * carousel, the thumbnails, the zoom and the video markup are all Horizon's.
 *
 * WHAT. When a slide in the PDP gallery becomes current and holds a
 * Shopify-hosted video, the video is loaded through Horizon's own
 * <deferred-media> (loadContent, without moving focus) and plays MUTED and
 * LOOPING. The slide it replaces is paused. Differences from the control, on
 * purpose: it is always muted until the shopper asks for sound (GG muted only
 * on iPhones), because sound that starts on a swipe is the thing people
 * leave pages over; and under prefers-reduced-motion nothing starts, the
 * poster and its play button stay, exactly as Horizon renders them.
 *
 * THE CONTROLS (session K11, 2026-10-01): two 44 px buttons in the video's
 * corner, from the section's <template data-wc-video-controls>, on every
 * video that has played, in the gallery and in the zoom:
 * - play/pause. Horizon's own toggle covers the whole video and is invisible
 *   until hovered, which a phone cannot do: a shopper had no visible way to
 *   pause (WCAG 2.2.2 wants one for anything moving for more than 5 s) and a
 *   paused video looked like a photograph. Tapping the video still works;
 *   Horizon's toggle is taken out of the tab order so the keyboard meets the
 *   one visible button, not two that do the same thing.
 * - sound, because Horizon renders gallery videos without controls.
 * Each keeps ONE label and says its state with aria-pressed (I-46). Each
 * carries an on:click Horizon's delegated handler resolves to (a method no
 * component has, so nothing runs) instead of the slide's zoom, or the zoomed
 * picture's close.
 *
 * A SLIDE IS SHOWN ONCE, NOT ON EVERY TAP (K11; Luke: "they only work on the
 * first click after the video is loaded"). Horizon's slideshow answers EVERY
 * pointerup inside it with a `slideshow:select`, for the slide that is
 * already current when the pointer did not travel (slideshow.js
 * onPointerUp). This file used to read each of those as a video slide
 * becoming current, so a tap on either button first re-muted and re-played
 * the video and only then ran the button: sound could only ever be turned
 * on, and play/pause could only ever pause. A keyboard press sends no
 * pointerup, which is why it worked from the keyboard. Now a select for the
 * slide this file already showed is ignored (`shown`).
 *
 * THE SHOPPER'S CHOICE WINS. What somebody chose on a video (paused it, or
 * turned its sound on) is kept by media id for the visit, so leaving the
 * slide and coming back, or a variant change, finds it as they left it;
 * I-28 starts a video, it never overrides a person.
 *
 * THE CONTROLS NEVER LIE. Horizon's toggle decides between play and pause on
 * its own `isPlaying` flag. Every control is kept in step with the VIDEO,
 * from its play, pause and volumechange events, so a browser that refuses
 * autoplay (an iPhone in Low Power Mode) cannot leave a button one press out
 * of step.
 *
 * LIGHTER ON A PHONE. Horizon's video_tag always serves the 1080p file
 * (4.8-7.2 Mbps here) and a 2500 px poster JPG on top of the photo already on
 * screen. The section writes every video's MP4 renditions into
 * <script data-wc-video-sources> (Shopify keeps 480p, 720p and 1080p), and
 * before a template is ever cloned this file points it at the smallest one
 * that fills the slide at up to 2x density, and at most 720p on a phone
 * (about 40% fewer bytes than 1080p); Save-Data takes the smallest. The
 * poster becomes the slide's own photo at a matching size.
 *
 * ONE TAB STOP PER CONTROL, AND ONLY ON THE SLIDE THAT SHOWS. Horizon leaves
 * hidden slides (aria-hidden="true") in the tab order and MOVES the gallery
 * to whatever gets focus, so a keyboard user tabbing past a video jumped to
 * the next one. Controls inside hidden slides, the poster button of a video
 * already playing, and Horizon's invisible toggle are taken out of the tab
 * order (tabindex -1, never inert: a pointer and a script can still reach
 * them) and given back the moment that stops being true.
 *
 * Horizon REPLACES the whole <media-gallery> on every variant change, so
 * everything here listens on document and looks elements up afresh.
 */

const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const phone = window.matchMedia("(max-width: 749px)");
const GALLERY = ".wc-pdp media-gallery";
const PHONE_MAX_HEIGHT = 720;

/** @type {Set<HTMLElement>} deferred-media elements this file started */
const started = new Set();

/** @type {WeakMap<Element, Element>} the slide each slideshow last showed, per slideshow */
const shown = new WeakMap();

/** @type {Map<string, { paused?: boolean, sound?: boolean }>} the shopper's choices, by media id */
const choices = new Map();

/** The <video> a shopper sees: Horizon's clone is appended last. */
function presentedVideo(media) {
  const videos = media.querySelectorAll("video");
  return videos[videos.length - 1] || null;
}

/** A Shopify-hosted video waiting in this slide, or null. YouTube and Vimeo are left alone. */
function videoMedia(slide) {
  const media = slide && slide.querySelector("deferred-media");
  if (!media) return null;
  if (presentedVideo(media)) return media;
  const template = media.querySelector("template");
  return template && template.content.querySelector("video") ? media : null;
}

/** The media id the main gallery and the zoom share, or null. */
function mediaKey(media) {
  const holder = media.closest("[data-media-id]");
  return holder ? holder.getAttribute("data-media-id") : null;
}

function choose(media, choice) {
  const key = mediaKey(media);
  if (key) choices.set(key, { ...choices.get(key), ...choice });
}

function zoomOpen(gallery) {
  return !!(gallery && gallery.querySelector("dialog[open]"));
}

/* ------------------------------------------------------------ lighter files */

/** @type {Record<string, { url: string, width: number }[]> | null} */
let sources = null;
function videoSources() {
  if (sources) return sources;
  sources = {};
  for (const node of document.querySelectorAll("script[data-wc-video-sources]")) {
    try {
      Object.assign(sources, JSON.parse(node.textContent || "{}"));
    } catch {
      // A broken block costs only the lighter file: Horizon's own source stays.
    }
  }
  return sources;
}

/** The rendition to serve for a slide `cssWidth` px wide, or null to keep Horizon's. */
function pickRendition(list, cssWidth) {
  const mp4s = (list || []).filter((s) => s && s.url && s.width > 0).sort((a, b) => a.width - b.width);
  if (!mp4s.length) return null;
  const connection = /** @type {any} */ (navigator).connection;
  if (connection && connection.saveData) return mp4s[0];
  const want = cssWidth * Math.min(window.devicePixelRatio || 1, 2);
  let pick = mp4s.find((s) => s.width >= want) || mp4s[mp4s.length - 1];
  if (phone.matches) {
    const capped = mp4s.filter((s) => s.width <= PHONE_MAX_HEIGHT);
    if (capped.length && pick.width > PHONE_MAX_HEIGHT) pick = capped[capped.length - 1];
  }
  return pick;
}

/** The slide's own photo at `width`, from the URL Horizon already renders for it. */
function photoUrl(media, width) {
  const holder = media.closest(".product-media");
  const img = holder && holder.querySelector(":scope > .product-media__image");
  if (!img) return null;
  if (img.currentSrc) return img.currentSrc;
  const src = img.getAttribute("src");
  if (!src || !/[?&]width=\d+/.test(src)) return null;
  return src.replace(/([?&]width=)\d+/, `$1${Math.ceil(width / 100) * 100}`);
}

/**
 * Point a not-yet-cloned template at the right file and a light poster. Done
 * once per element, before Horizon's loadContent clones it: a template's
 * content is never fetched, so changing it costs nothing.
 */
function lighten(media) {
  if (media.dataset.wcLight || media.getAttribute("data-media-loaded")) return;
  const template = media.querySelector("template");
  const video = template && template.content.querySelector("video");
  if (!video) return;
  media.dataset.wcLight = "1";
  const width = media.getBoundingClientRect().width || window.innerWidth;
  const pick = pickRendition(videoSources()[mediaKey(media) || ""], width);
  if (pick) {
    const source = video.querySelector('source[type="video/mp4"]') || video.querySelector("source");
    if (source) source.setAttribute("src", pick.url);
    else video.setAttribute("src", pick.url);
  }
  const poster = photoUrl(media, width * Math.min(window.devicePixelRatio || 1, 2));
  if (poster) video.setAttribute("poster", poster);
}

/* ------------------------------------------------------------ the controls */

function controlsOf(media) {
  const host = media.parentElement;
  return host ? host.querySelector(":scope > .wc-gallery__controls") : null;
}

/** Both buttons say what the VIDEO is doing, never what they last set. */
function syncControls(media) {
  const controls = controlsOf(media);
  const video = presentedVideo(media);
  if (!controls || !video) return;
  const sound = controls.querySelector(".wc-gallery__sound");
  if (sound) {
    const on = !video.muted;
    sound.setAttribute("aria-pressed", String(on));
    sound.dataset.sound = on ? "on" : "off";
  }
  const play = controls.querySelector(".wc-gallery__play");
  if (play) {
    play.setAttribute("aria-pressed", String(video.paused));
    play.dataset.state = video.paused ? "paused" : "playing";
  }
}

function ensureControls(media) {
  const host = media.parentElement;
  if (!host || !presentedVideo(media)) return;
  if (!controlsOf(media)) {
    const template = document.querySelector("template[data-wc-video-controls]");
    const node = template && template.content.firstElementChild;
    if (!node) return;
    const controls = /** @type {HTMLElement} */ (node.cloneNode(true));
    controls.querySelector(".wc-gallery__sound")?.addEventListener("click", (event) => {
      event.preventDefault();
      const video = presentedVideo(media);
      if (!video) return;
      // Sound only: playback is the other button's.
      video.muted = !video.muted;
      choose(media, { sound: !video.muted });
      syncControls(media);
    });
    controls.querySelector(".wc-gallery__play")?.addEventListener("click", (event) => {
      event.preventDefault();
      const video = presentedVideo(media);
      if (!video) return;
      // Through Horizon, so its own flag, glyph and "one video at a time" hold.
      if (video.paused) /** @type {any} */ (media).playMedia();
      else /** @type {any} */ (media).pauseMedia();
      choose(media, { paused: video.paused });
      syncControls(media);
    });
    host.appendChild(controls);
  }
  syncControls(media);
  syncTabStops(media.closest("slideshow-component, dialog") || media);
}

/** Horizon's toggle shows the glyph for what a press will do; keep it on the video's real state. */
function syncToggle(media) {
  const video = presentedVideo(media);
  if (!video || !media.getAttribute("data-media-loaded")) return;
  media.isPlaying = !video.paused;
  if (typeof media.updatePlayPauseHint === "function") media.updatePlayPauseHint(!video.paused);
}

/* ------------------------------------------------------------ the tab order */

const FOCUSABLE = "button, a[href], input, select, textarea, video[controls], [tabindex]";

/** Whether `el` should be out of the tab order right now, and why it is in this file at all. */
function shouldSkip(el) {
  const slide = el.closest("slideshow-slide");
  if (slide && slide.getAttribute("aria-hidden") === "true") return true;
  const media = el.closest("deferred-media");
  if (!media || !media.getAttribute("data-media-loaded")) return false;
  // A poster button on a video that already plays does nothing; Horizon's
  // invisible toggle duplicates the visible play/pause button beside it.
  if (el.matches('[ref="deferredMediaPlayButton"]')) return true;
  return el.matches('[ref="toggleMediaButton"]') && !!controlsOf(media);
}

function syncTabStops(root) {
  if (!root || !root.querySelectorAll) return;
  for (const el of root.querySelectorAll(FOCUSABLE)) {
    if (el.closest(".wc-gallery__controls") && !el.closest('slideshow-slide[aria-hidden="true"]')) {
      // Ours: in the order unless its slide is hidden.
      if (el.hasAttribute("data-wc-tab")) restoreTab(el);
      continue;
    }
    if (shouldSkip(el)) {
      if (!el.hasAttribute("data-wc-tab")) el.setAttribute("data-wc-tab", el.getAttribute("tabindex") ?? "");
      el.setAttribute("tabindex", "-1");
    } else if (el.hasAttribute("data-wc-tab")) {
      restoreTab(el);
    }
  }
}

function restoreTab(el) {
  const was = el.getAttribute("data-wc-tab");
  el.removeAttribute("data-wc-tab");
  if (was) el.setAttribute("tabindex", was);
  else el.removeAttribute("tabindex");
}

/* ------------------------------------------------------------ starting */

function start(slide) {
  if (reduceMotion.matches) return;
  const media = videoMedia(slide);
  if (!media) return;
  lighten(media);
  if (!media.getAttribute("data-media-loaded") && typeof media.loadContent === "function") {
    media.loadContent(false);
  }
  const video = presentedVideo(media);
  if (!video) return;
  const choice = choices.get(mediaKey(media) || "") || {};
  video.loop = true;
  video.playsInline = true;
  video.muted = !choice.sound;
  started.add(media);
  ensureControls(media);
  if (choice.paused) {
    // loadContent plays an autoplay clone by itself; a shopper who paused this
    // video gets it back paused.
    video.pause();
    syncToggle(media);
    return;
  }
  video
    .play()
    .catch(() => {
      // Declined on autoplay-policy grounds: the play button offers Play, which works.
    })
    .finally(() => {
      syncToggle(media);
      syncControls(media);
    });
  syncToggle(media);
}

function pauseOthers(slide) {
  for (const media of started) {
    if (!media.isConnected) {
      started.delete(media);
    } else if (!slide || !slide.contains(media)) {
      if (typeof media.pauseMedia === "function") media.pauseMedia();
    }
  }
}

document.addEventListener("slideshow:select", (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const gallery = target.closest(GALLERY);
  if (!gallery || target.closest("dialog")) return;
  const slide = /** @type {any} */ (event).detail && /** @type {any} */ (event).detail.slide;
  // A tap, a press of either button, a drag that snapped back: Horizon says
  // "select" for the slide that is already showing. That is not a slide being
  // shown, and acting on it is the bug described at the top of this file.
  if (!slide || shown.get(target) === slide) return;
  shown.set(target, slide);
  pauseOthers(slide);
  // The zoom's thumbnails move this gallery too. Behind the open dialog
  // nothing starts: its "started playing" event would pause the video the
  // shopper is watching in the zoom. The dialog's close starts it instead.
  if (!zoomOpen(gallery)) start(slide);
});

/* The first slide, when it is a video: start it once the gallery is on screen. */
const onScreen = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      onScreen.unobserve(entry.target);
      const current = entry.target.querySelector('slideshow-slide[aria-hidden="false"]');
      const slideshow = current && current.closest("slideshow-component");
      if (slideshow) shown.set(slideshow, current);
      start(current);
    }
  },
  { threshold: 0.5 },
);

function watch(root) {
  for (const gallery of root.querySelectorAll(GALLERY)) {
    onScreen.observe(gallery);
    // Every video's template, the zoom's included, before anything clones it.
    for (const media of gallery.querySelectorAll("deferred-media")) lighten(media);
    syncTabStops(gallery);
  }
}
watch(document);

/* A replaced gallery (variant change) is watched again; a slide shown or hidden updates the tab order. */
new MutationObserver((records) => {
  for (const record of records) {
    if (record.type === "attributes") {
      const slide = /** @type {Element} */ (record.target);
      if (slide.matches("slideshow-slide") && slide.closest(GALLERY)) syncTabStops(slide);
      continue;
    }
    for (const node of record.addedNodes) {
      if (node instanceof Element && (node.matches("media-gallery") || node.querySelector("media-gallery"))) {
        watch(node.parentElement || document);
      }
    }
  }
}).observe(document.querySelector(".wc-pdp") || document.body, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ["aria-hidden"],
});

/* Asking for reduced motion mid-visit stops what this file started. */
reduceMotion.addEventListener("change", () => {
  if (reduceMotion.matches) pauseOthers(null);
});

/*
 * The video is the truth. These events do not bubble, so they are caught on
 * the way down. A video that plays gets the controls wherever it is (the
 * zoom, or a poster pressed under reduced motion, where nothing of this file
 * started it), and every control follows every play, pause and volume change.
 */
function galleryMedia(event) {
  const video = event.target;
  if (!(video instanceof HTMLVideoElement) || !video.closest(GALLERY)) return null;
  const media = video.closest("deferred-media");
  return media && presentedVideo(media) === video ? media : null;
}

document.addEventListener(
  "play",
  (event) => {
    const media = galleryMedia(event);
    if (!media) return;
    ensureControls(media);
    syncToggle(media);
  },
  true,
);

for (const type of ["pause", "volumechange"]) {
  document.addEventListener(
    type,
    (event) => {
      const media = galleryMedia(event);
      if (!media) return;
      syncToggle(media);
      syncControls(media);
    },
    true,
  );
}

/*
 * A press of Horizon's play/pause toggle (a tap on the video) is a choice.
 * Horizon's own handler runs first (it listens in the capture phase), so by
 * the time the click bubbles here the video says what the shopper chose.
 */
document.addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const toggle = target.closest('[ref="toggleMediaButton"]');
  const media = toggle && toggle.closest("deferred-media");
  if (!media || !media.closest(GALLERY)) return;
  const video = presentedVideo(media);
  if (video) choose(media, { paused: video.paused });
});

/*
 * THE SLIDE ON SCREEN IS THE SLIDE SHOWN (K11; post-launch, the mother 8 Oct).
 * Horizon's slideshow.js #sync() takes the current slide from an
 * IntersectionObserver cache on each scroll event, and its scroll-end handler
 * refreshes that cache without syncing again. When the last scroll event
 * beats the observer (seen in a full test run at normal load, 8 Oct), the
 * swipe lands on one slide while Horizon keeps the one before: the counter,
 * the dots and the thumbnails name the old slide, the video on screen never
 * starts, and the video swiped AWAY keeps playing, with its sound if the
 * shopper had turned it on, until the next swipe. So when the track comes to
 * rest, the slide nearest its left edge is the current one: if Horizon says
 * otherwise, it is told, through its own public select(), which moves its
 * index and fires the `slideshow:select` this file already answers. select()
 * exposes the new slide and never hides the old one, so aria-hidden is set
 * from the same geometry Horizon's observer uses (half or more of a slide on
 * screen), and only when it disagrees.
 */
function onScreenSlides(slideshow) {
  const scroller = slideshow.querySelector('[ref="scroller"]');
  const slides = /** @type {HTMLElement[]} */ ([...slideshow.querySelectorAll("slideshow-slide")]);
  if (!scroller || !slides.length) return null;
  const box = scroller.getBoundingClientRect();
  let nearest = 0;
  let best = Infinity;
  const visible = slides.map((slide, i) => {
    const r = slide.getBoundingClientRect();
    const d = Math.abs(r.left - box.left);
    if (d < best) [best, nearest] = [d, i];
    const shownWidth = Math.min(r.right, box.right) - Math.max(r.left, box.left);
    return r.width > 0 && shownWidth >= r.width / 2;
  });
  return { slides, nearest, visible };
}

function resync(slideshow) {
  const gallery = slideshow.closest(GALLERY);
  if (!gallery || slideshow.closest("dialog") || zoomOpen(gallery)) return;
  if (slideshow.hasAttribute("dragging") || slideshow.hasAttribute("transitioning")) return;
  const seen = onScreenSlides(slideshow);
  const ss = /** @type {any} */ (slideshow);
  if (!seen || typeof ss.select !== "function") return;
  // ONLY when the track and Horizon's index disagree. Otherwise nothing here
  // touches the page, aria-hidden included (the mother, 8 Oct).
  if (ss.current === seen.nearest) return;
  // A trace for QA, read by the tests and visible in DevTools: how many times
  // this corrected Horizon on this gallery, and the last correction (from>to).
  const count = Number((slideshow.getAttribute("data-wc-resync") || "0").split(":")[0]) + 1;
  slideshow.setAttribute("data-wc-resync", `${count}:${ss.current}>${seen.nearest}`);
  ss.select(seen.nearest, undefined, { animate: false });
  seen.slides.forEach((slide, i) => {
    const hidden = String(!seen.visible[i]);
    if (slide.getAttribute("aria-hidden") !== hidden) slide.setAttribute("aria-hidden", hidden);
  });
}

/*
 * ONLY A LAG THAT HOLDS STILL is corrected. Horizon's select() (an arrow, a
 * dot, a thumbnail, the keyboard, the zoom) moves its index FIRST and scrolls
 * after, so for a moment the index can be right and the track not there yet.
 * Correcting in that moment would undo the shopper's press. No test has caught
 * that happening (the browser fires the new scroll before an animation-frame
 * re-sync runs), so this is a guard, not a fix: the index and the track are
 * noted when the scroll ends and read again 150 ms later, and if either moved,
 * someone is navigating and this stays out of the way.
 */
const RESYNC_AFTER_MS = 150;

function settleThenResync(slideshow) {
  const ss = /** @type {any} */ (slideshow);
  const scroller = slideshow.querySelector('[ref="scroller"]');
  if (!scroller) return;
  const index = ss.current;
  const left = scroller.scrollLeft;
  window.setTimeout(() => {
    if (ss.current !== index || scroller.scrollLeft !== left) return;
    resync(slideshow);
  }, RESYNC_AFTER_MS);
}

/*
 * WHEN: the moment Horizon itself says a scroll has ended, which is its
 * scroll-end handler taking `transitioning` off the slideshow (that handler
 * refreshes the cache and is exactly the step that forgets to sync). Not the
 * browser's `scrollend`: it fires BEFORE Horizon's handler, while
 * `transitioning` is still set, and Safari before 18.2 has no such event.
 */
new MutationObserver((records) => {
  for (const record of records) {
    const slideshow = /** @type {Element} */ (record.target);
    if (slideshow.hasAttribute("transitioning") || !slideshow.closest(GALLERY)) continue;
    settleThenResync(slideshow);
  }
}).observe(document.querySelector(".wc-pdp") || document.body, {
  subtree: true,
  attributes: true,
  attributeFilter: ["transitioning"],
});

/* The zoom closed: the slide it leaves showing is shown now. */
window.addEventListener("dialog:close", () => {
  // After Horizon's own listeners, which pause every video on this event.
  setTimeout(() => {
    for (const gallery of document.querySelectorAll(GALLERY)) {
      if (zoomOpen(gallery)) continue;
      start(gallery.querySelector('slideshow-component slideshow-slide[aria-hidden="false"]'));
    }
  }, 0);
});
